/**
 * Pending Assignment Notifications
 *
 * Lists every crew member with un-notified assignments in the current org and
 * lets the admin send a single consolidated notification per person, dismiss
 * the pending state without emailing, or cancel the assignment(s) outright.
 */

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  listPendingNotifications,
  notifyUser,
  notifyAllPending,
  dismissPendingNotifications,
  cancelPendingAssignments,
  type PendingNotificationGroup,
} from '../../api/assignments';
import { getErrorMessage } from '../../api/client';
import { ConfirmDialog } from '../ui';
import CrewProfileLink from '../crew/CrewProfileLink';
import { formatShortDateWithWeekday, formatInTimeZone } from '../../utils/dateFormatting';

function formatEventDate(iso: string): string {
  return formatShortDateWithWeekday(iso);
}

function formatTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return formatInTimeZone(iso, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

type ConfirmTarget =
  | { kind: 'dismiss-user'; userId: string; userName: string; count: number }
  | { kind: 'cancel-user'; userId: string; userName: string; count: number }
  | { kind: 'cancel-assignment'; assignmentId: string; eventName: string; userName: string };

export default function PendingAssignmentNotifications() {
  const [groups, setGroups] = useState<PendingNotificationGroup[]>([]);
  const [loading, setLoading] = useState(true);
  // Tracks an in-progress manual refresh separately from `loading` so the
  // current list stays visible (and the Refresh button shows a busy state)
  // instead of the whole section flashing to a placeholder.
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sendingFor, setSendingFor] = useState<string | null>(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<ConfirmTarget | null>(null);
  const [actingOnUser, setActingOnUser] = useState<string | null>(null);
  const [actingOnAssignment, setActingOnAssignment] = useState<string | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const load = useCallback(async ({ isRefresh = false } = {}) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const { groups } = await listPendingNotifications();
      setGroups(groups);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      if (isRefresh) setRefreshing(false);
      else setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (userId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // NOTE on the optimistic removal below (Dylan, 2026-08-10): sending only
  // ENQUEUES the notification — the worker marks assignments notified a few
  // seconds later. An immediate refetch therefore still sees them pending,
  // and the row lingering after "sent" read as "the message never went".
  // So: remove the row locally on successful queueing. A send that
  // ultimately fails (after the worker's retries) leaves notificationSentAt
  // null, so the row reappears on the next visit — failures resurface,
  // successes never flicker.

  const handleSendAll = async () => {
    setConfirmAllOpen(false);
    setSendingAll(true);
    try {
      const count = groups.length;
      await notifyAllPending();
      setGroups([]);
      toast.success(
        `Notifications on their way to ${count} crew member${count !== 1 ? 's' : ''}.`
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSendingAll(false);
    }
  };

  const handleSend = async (group: PendingNotificationGroup) => {
    setSendingFor(group.user.id);
    try {
      const result = await notifyUser(group.user.id);
      setGroups((prev) => prev.filter((g) => g.user.id !== group.user.id));
      toast.success(
        `Notification with ${result.count} assignment${result.count !== 1 ? 's' : ''} on its way to ${group.user.firstName} ${group.user.lastName}.`
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSendingFor(null);
    }
  };

  // Dismiss a single assignment (no confirm — assignment stays, just marked notified).
  const handleDismissAssignment = async (assignmentId: string, eventName: string) => {
    setActingOnAssignment(assignmentId);
    try {
      await dismissPendingNotifications({ assignmentIds: [assignmentId] });
      toast.success(`Dismissed pending notification for ${eventName}.`);
      await load({ isRefresh: true });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setActingOnAssignment(null);
    }
  };

  // Dismiss all of one crew member's pending notifications (confirmed).
  const handleDismissUser = async (target: Extract<ConfirmTarget, { kind: 'dismiss-user' }>) => {
    setConfirmLoading(true);
    setActingOnUser(target.userId);
    try {
      const result = await dismissPendingNotifications({ userId: target.userId });
      toast.success(
        result.count === 0
          ? 'Nothing to dismiss.'
          : `Dismissed ${result.count} pending notification${result.count !== 1 ? 's' : ''} for ${target.userName}.`
      );
      await load({ isRefresh: true });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setConfirmLoading(false);
      setActingOnUser(null);
      setPendingAction(null);
    }
  };

  // Cancel one pending assignment outright (confirmed).
  const handleCancelAssignment = async (
    target: Extract<ConfirmTarget, { kind: 'cancel-assignment' }>
  ) => {
    setConfirmLoading(true);
    setActingOnAssignment(target.assignmentId);
    try {
      const result = await cancelPendingAssignments({ assignmentIds: [target.assignmentId] });
      toast.success(
        result.count === 0
          ? 'Nothing to cancel.'
          : `Cancelled ${target.userName}'s assignment for ${target.eventName}.`
      );
      await load({ isRefresh: true });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setConfirmLoading(false);
      setActingOnAssignment(null);
      setPendingAction(null);
    }
  };

  // Cancel all of one crew member's pending assignments (confirmed).
  const handleCancelUser = async (target: Extract<ConfirmTarget, { kind: 'cancel-user' }>) => {
    setConfirmLoading(true);
    setActingOnUser(target.userId);
    try {
      const result = await cancelPendingAssignments({ userId: target.userId });
      toast.success(
        result.count === 0
          ? 'Nothing to cancel.'
          : `Cancelled ${result.count} assignment${result.count !== 1 ? 's' : ''} for ${target.userName}.`
      );
      await load({ isRefresh: true });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setConfirmLoading(false);
      setActingOnUser(null);
      setPendingAction(null);
    }
  };

  const handleConfirm = () => {
    if (!pendingAction) return;
    if (pendingAction.kind === 'dismiss-user') return handleDismissUser(pendingAction);
    if (pendingAction.kind === 'cancel-user') return handleCancelUser(pendingAction);
    if (pendingAction.kind === 'cancel-assignment') return handleCancelAssignment(pendingAction);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Pending Notifications</h2>
        <p className="text-sm text-gray-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">Pending Notifications</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Crew members who have assignments but haven't been notified yet. Send a single
            email per person, dismiss without emailing, or cancel an assignment.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => load({ isRefresh: true })}
            disabled={refreshing}
            className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-60 disabled:cursor-wait"
            title="Refresh"
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            onClick={() => setConfirmAllOpen(true)}
            disabled={sendingAll || groups.length === 0}
            className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {sendingAll
              ? 'Sending all…'
              : `Send All${groups.length > 0 ? ` (${groups.length})` : ''}`}
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
          {error}
        </div>
      )}

      {groups.length === 0 ? (
        <div className="px-6 py-8 text-center text-sm text-gray-500">
          All caught up. No pending notifications.
        </div>
      ) : (
        <ul className="divide-y divide-gray-200">
          {groups.map((g) => {
            const count = g.assignments.length;
            const isOpen = expanded.has(g.user.id);
            const isSending = sendingFor === g.user.id;
            const isActingOnGroup = actingOnUser === g.user.id;
            const userName = `${g.user.firstName} ${g.user.lastName}`;
            return (
              <li key={g.user.id} className="px-4 sm:px-6 py-4">
                {/* Header row: avatar + identity + count. On mobile this is its
                    own line so the action buttons can stack underneath instead
                    of overflowing the row. */}
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {g.user.firstName.charAt(0)}
                    {g.user.lastName.charAt(0)}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      <CrewProfileLink userId={g.user.id}>{userName}</CrewProfileLink>
                    </p>
                    <button
                      type="button"
                      onClick={() => toggle(g.user.id)}
                      className="block max-w-full text-left text-xs text-gray-500 truncate"
                      aria-label={isOpen ? 'Hide assignments' : 'Show assignments'}
                    >
                      {g.user.email}
                    </button>
                  </div>
                  <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 whitespace-nowrap">
                    {count} pending
                  </span>
                  <button
                    type="button"
                    onClick={() => toggle(g.user.id)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                    aria-label={isOpen ? 'Hide assignments' : 'Show assignments'}
                  >
                    {isOpen ? 'Hide' : 'Show'}
                  </button>
                </div>

                {/* Action buttons: wrap nicely on small screens; on sm+ they
                    sit on a single line right-aligned under the header. */}
                <div className="mt-3 flex flex-wrap gap-2 sm:justify-end">
                  <button
                    type="button"
                    onClick={() =>
                      setPendingAction({
                        kind: 'dismiss-user',
                        userId: g.user.id,
                        userName,
                        count,
                      })
                    }
                    disabled={isSending || isActingOnGroup}
                    className="px-2.5 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    title="Mark notified without sending email"
                  >
                    Dismiss
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setPendingAction({
                        kind: 'cancel-user',
                        userId: g.user.id,
                        userName,
                        count,
                      })
                    }
                    disabled={isSending || isActingOnGroup}
                    className="px-2.5 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 hover:bg-red-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    title="Cancel all pending assignments for this crew member"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSend(g)}
                    disabled={isSending || isActingOnGroup}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSending ? 'Sending…' : 'Send Notification'}
                  </button>
                </div>

                {isOpen && (
                  <ul className="mt-3 ml-14 space-y-2">
                    {g.assignments.map((a) => {
                      const callTime = formatTime(a.event.callTime);
                      const startTime = formatTime(a.event.startTime);
                      const isActingOnRow = actingOnAssignment === a.id;
                      return (
                        <li
                          key={a.id}
                          className="text-sm text-gray-700 flex flex-wrap items-center gap-x-2 gap-y-1"
                        >
                          <span className="font-medium">{a.event.name}</span>
                          <span className="text-gray-500">— {a.role.roleName}</span>
                          <span className="text-gray-500">·</span>
                          <span className="text-gray-500">{formatEventDate(a.event.eventDate)}</span>
                          {(callTime || startTime) && (
                            <>
                              <span className="text-gray-500">·</span>
                              <span className="text-gray-500">
                                {callTime && `Call ${callTime}`}
                                {callTime && startTime && ' / '}
                                {startTime && `Start ${startTime}`}
                              </span>
                            </>
                          )}
                          <span className="text-gray-400">@ {a.event.venue}</span>
                          <span className="ml-auto flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleDismissAssignment(a.id, a.event.name)}
                              disabled={isActingOnRow || isActingOnGroup}
                              className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Mark notified without sending email"
                            >
                              Dismiss
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              type="button"
                              onClick={() =>
                                setPendingAction({
                                  kind: 'cancel-assignment',
                                  assignmentId: a.id,
                                  eventName: a.event.name,
                                  userName,
                                })
                              }
                              disabled={isActingOnRow || isActingOnGroup}
                              className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Cancel this assignment"
                            >
                              Cancel
                            </button>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        isOpen={confirmAllOpen}
        onClose={() => setConfirmAllOpen(false)}
        onConfirm={handleSendAll}
        title="Send all pending notifications?"
        message={`This will send one email to each of ${groups.length} crew member${
          groups.length !== 1 ? 's' : ''
        }, covering every pending assignment they have. Each email is marked as sent and won't be re-sent automatically.`}
        confirmLabel={`Send to ${groups.length} crew member${groups.length !== 1 ? 's' : ''}`}
        cancelLabel="Cancel"
        variant="default"
        isLoading={sendingAll}
      />

      <ConfirmDialog
        isOpen={pendingAction?.kind === 'dismiss-user'}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirm}
        title="Dismiss pending notifications?"
        message={
          pendingAction?.kind === 'dismiss-user'
            ? `Mark all ${pendingAction.count} pending notification${
                pendingAction.count !== 1 ? 's' : ''
              } for ${pendingAction.userName} as sent, WITHOUT emailing them. Use this if you've already informed them another way.`
            : ''
        }
        confirmLabel="Dismiss"
        cancelLabel="Keep pending"
        variant="default"
        isLoading={confirmLoading}
      />

      <ConfirmDialog
        isOpen={pendingAction?.kind === 'cancel-user'}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirm}
        title="Cancel pending assignments?"
        message={
          pendingAction?.kind === 'cancel-user'
            ? `Delete ${pendingAction.count} pending assignment${
                pendingAction.count !== 1 ? 's' : ''
              } for ${pendingAction.userName}. They were never notified, so no email will be sent. This cannot be undone.`
            : ''
        }
        confirmLabel="Cancel assignments"
        cancelLabel="Keep them"
        variant="danger"
        isLoading={confirmLoading}
      />

      <ConfirmDialog
        isOpen={pendingAction?.kind === 'cancel-assignment'}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirm}
        title="Cancel this assignment?"
        message={
          pendingAction?.kind === 'cancel-assignment'
            ? `Delete ${pendingAction.userName}'s assignment for ${pendingAction.eventName}. They were never notified, so no email will be sent. This cannot be undone.`
            : ''
        }
        confirmLabel="Cancel assignment"
        cancelLabel="Keep it"
        variant="danger"
        isLoading={confirmLoading}
      />
    </div>
  );
}
